import type { DriverRow } from "@/types/driver";
import { greenColor, redColor, yellowColor } from "@/utils/color";
import { isWorkspaceMembershipOffline } from "@/utils/workspaceMembershipOffline";

/**
 * Whether the hands-free heartbeat is missing or stale (unreachable via HF).
 *
 * Delegates to `isWorkspaceMembershipOffline` so the threshold stays aligned with
 * `offlineThreshold` / `sqlMembershipIsOffline` and workspace status transforms.
 */
export function isHandsFreeHeartbeatOffline(handsFreeUpdatedAt: string | Date | null | undefined): boolean {
  return isWorkspaceMembershipOffline({ handsFreeUpdatedAt });
}

export type HandsFreeAvailabilityDisplay = {
  label: string;
  tooltip: string;
  color: string;
};

type AvailabilitySource = Pick<
  DriverRow,
  "calculatedAvailability" | "mostRecentHandsFreeStatusEnabled" | "mostRecentVolumeAudible"
>;

/**
 * Maps backend availability + hands-free + mute state to the dispatcher-facing
 * label and tooltip used in the DriverView "Hands-free Availability" column.
 *
 * `Unavailable` (`notPresent`) is split into displays:
 *   - hands-free OFF → "Hands-free OFF" (precedence over mute)
 *   - hands-free ON and volume explicitly inaudible → "Device muted"
 *   - otherwise → "Hands-free ON" (same display as availability `unknown`)
 */
export function formatHandsFreeAvailability(driver: AvailabilitySource): HandsFreeAvailabilityDisplay | null {
  const handsFreeOnButNotInCab: HandsFreeAvailabilityDisplay = {
    label: "Hands-free ON",
    tooltip: "Driver has hands-free ON but may not be in the cab",
    color: yellowColor,
  };

  switch (driver.calculatedAvailability) {
    case "present":
      return {
        label: "Available",
        tooltip: "Driver is in the cab and hands-free is ON",
        color: greenColor,
      };
    case "unknown":
      return handsFreeOnButNotInCab;
    case "notPresent":
      if (driver.mostRecentHandsFreeStatusEnabled === false) {
        return {
          label: "Hands-free OFF",
          tooltip: "Driver has hands-free OFF",
          color: redColor,
        };
      }
      if (driver.mostRecentHandsFreeStatusEnabled === true && driver.mostRecentVolumeAudible === false) {
        return {
          label: "Device muted",
          tooltip: "Driver has hands-free ON but their device is muted",
          color: redColor,
        };
      }
      return handsFreeOnButNotInCab;
    default:
      return null;
  }
}
